/**
 * useCompletions Hook
 * Convenience hook for completion operations with helpful utilities
 */

import { useCompletionContext } from '../contexts/CompletionContext';
import { CompletionEntry } from '../models/CompletionEntry';
import { getTodayDateString, getLastNDays, formatDateForDisplay } from '../utils/dateUtils';

export const useCompletions = () => {
  const context = useCompletionContext();

  /**
   * Get today's completion for a specific goal
   */
  const getTodayCompletion = (goalId: string): CompletionEntry | undefined => {
    const today = getTodayDateString();
    return context.getCompletion(goalId, today);
  };

  /**
   * Check if goal is completed today
   */
  const isCompletedToday = (goalId: string): boolean => {
    const today = getTodayDateString();
    return context.isCompleted(goalId, today);
  };

  /**
   * Toggle today's completion for a goal
   */
  const toggleToday = async (accountId: string, goalId: string): Promise<void> => {
    const today = getTodayDateString();
    await context.toggleCompletion(accountId, goalId, today);
  };

  /**
   * Mark today as complete for a goal
   */
  const markTodayComplete = async (accountId: string, goalId: string): Promise<void> => {
    const today = getTodayDateString();
    await context.markComplete(accountId, goalId, today);
  };

  /**
   * Mark today as uncomplete for a goal
   */
  const markTodayUncomplete = async (accountId: string, goalId: string): Promise<void> => {
    const today = getTodayDateString();
    await context.markUncomplete(accountId, goalId, today);
  };

  /**
   * Get completions for last N days
   */
  const getCompletionsForLastNDays = (goalId: string, n: number): CompletionEntry[] => {
    const dates = getLastNDays(n);
    const completions: CompletionEntry[] = [];

    for (const date of dates) {
      const completion = context.getCompletion(goalId, date);
      if (completion) {
        completions.push(completion);
      }
    }

    return completions;
  };

  /**
   * Calculate current streak (consecutive completed days including today)
   */
  const getCurrentStreak = (goalId: string): number => {
    const today = getTodayDateString();
    const goalCompletions = context.completions.get(goalId);

    if (!goalCompletions) {
      return 0;
    }

    let streak = 0;
    const dates = getLastNDays(365); // Check up to 1 year

    for (const date of dates) {
      const completion = goalCompletions.get(date);

      if (completion?.completed) {
        streak++;
      } else {
        // Streak broken
        break;
      }
    }

    return streak;
  };

  /**
   * Calculate longest streak for a goal
   */
  const getLongestStreak = (goalId: string): number => {
    const goalCompletions = context.completions.get(goalId);

    if (!goalCompletions) {
      return 0;
    }

    let maxStreak = 0;
    let currentStreak = 0;

    // Sort dates in ascending order
    const dates = Array.from(goalCompletions.keys()).sort();

    for (let i = 0; i < dates.length; i++) {
      const completion = goalCompletions.get(dates[i]);

      if (completion?.completed) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    }

    return maxStreak;
  };

  /**
   * Calculate previous streak (the streak before the current one)
   */
  const getPreviousStreak = (goalId: string): number => {
    const goalCompletions = context.completions.get(goalId);

    if (!goalCompletions) {
      return 0;
    }

    const dates = getLastNDays(365); // Check up to 1 year
    let currentStreakEnded = false;
    let previousStreak = 0;

    for (const date of dates) {
      const completion = goalCompletions.get(date);

      if (completion?.completed) {
        if (currentStreakEnded) {
          // We're now counting the previous streak
          previousStreak++;
        }
        // If not ended yet, we're still in current streak, skip
      } else {
        // Hit a gap
        if (!currentStreakEnded) {
          // This marks the end of the current streak
          currentStreakEnded = true;
        } else if (previousStreak > 0) {
          // We've already found the previous streak, stop here
          break;
        }
      }
    }

    return previousStreak;
  };

  /**
   * Get days since last completion
   */
  const getDaysSinceLastCompletion = (goalId: string): number => {
    const goalCompletions = context.completions.get(goalId);

    if (!goalCompletions) {
      return 0;
    }

    const dates = getLastNDays(365); // Check up to 1 year

    for (let i = 0; i < dates.length; i++) {
      const completion = goalCompletions.get(dates[i]);

      if (completion?.completed) {
        // Found the last completion, return how many days ago
        return i;
      }
    }

    // No completions found in last year
    return 0;
  };

  /**
   * Get completion percentage for last N days
   */
  const getCompletionRate = (goalId: string, lastNDays: number): number => {
    const dates = getLastNDays(lastNDays);
    let completedCount = 0;

    for (const date of dates) {
      if (context.isCompleted(goalId, date)) {
        completedCount++;
      }
    }

    return lastNDays > 0 ? (completedCount / lastNDays) * 100 : 0;
  };

  /**
   * Get total completion count for a goal
   */
  const getTotalCompletions = (goalId: string): number => {
    const goalCompletions = context.completions.get(goalId);

    if (!goalCompletions) {
      return 0;
    }

    let count = 0;
    for (const completion of goalCompletions.values()) {
      if (completion.completed) {
        count++;
      }
    }

    return count;
  };

  /**
   * Get all completion dates for a goal (sorted most recent first)
   */
  const getCompletionDates = (goalId: string): string[] => {
    const goalCompletions = context.completions.get(goalId);

    if (!goalCompletions) {
      return [];
    }

    const dates: string[] = [];
    for (const [date, completion] of goalCompletions.entries()) {
      if (completion.completed) {
        dates.push(date);
      }
    }

    // Sort descending (most recent first)
    return dates.sort((a, b) => b.localeCompare(a));
  };

  /**
   * Check if goal has any completions
   */
  const hasAnyCompletions = (goalId: string): boolean => {
    return getTotalCompletions(goalId) > 0;
  };

  /**
   * Get completion status summary
   */
  const getCompletionSummary = (goalId: string) => {
    return {
      isCompletedToday: isCompletedToday(goalId),
      currentStreak: getCurrentStreak(goalId),
      previousStreak: getPreviousStreak(goalId),
      daysSinceLastCompletion: getDaysSinceLastCompletion(goalId),
      last7DaysRate: getCompletionRate(goalId, 7),
      last30DaysRate: getCompletionRate(goalId, 30),
    };
  };

  /**
   * Load completions for today (quick load for main screen)
   */
  const loadTodayCompletions = async (accountId: string, goalId: string): Promise<void> => {
    const today = getTodayDateString();
    await context.loadCompletionsForDateRange(accountId, goalId, [today]);
  };

  /**
   * Load recent completions (last N days)
   */
  const loadRecentCompletions = async (
    accountId: string,
    goalId: string,
    days: number = 30
  ): Promise<void> => {
    const dates = getLastNDays(days);
    await context.loadCompletionsForDateRange(accountId, goalId, dates);
  };

  /**
   * Get recent completions as array (for pattern detection)
   */
  const getRecentCompletions = (goalId: string, days: number = 365): CompletionEntry[] => {
    const goalCompletions = context.completions.get(goalId);
    if (!goalCompletions) {
      return [];
    }

    const dates = getLastNDays(days);
    const completions: CompletionEntry[] = [];

    for (const date of dates) {
      const completion = goalCompletions.get(date);
      if (completion) {
        completions.push(completion);
      } else {
        // Add uncompleted entry for proper pattern detection
        completions.push({
          id: `temp-${date}`,
          accountId: '',
          goalId,
          date,
          completed: false,
          timestamp: 0,
          syncStatus: 'local',
        });
      }
    }

    return completions;
  };

  return {
    // Context state and operations
    ...context,

    // Today shortcuts
    getTodayCompletion,
    isCompletedToday,
    toggleToday,
    markTodayComplete,
    markTodayUncomplete,
    loadTodayCompletions,

    // Recent data
    getCompletionsForLastNDays,
    loadRecentCompletions,
    getRecentCompletions,

    // Statistics
    getCurrentStreak,
    getLongestStreak,
    getPreviousStreak,
    getDaysSinceLastCompletion,
    getCompletionRate,
    getTotalCompletions,
    getCompletionDates,
    hasAnyCompletions,
    getCompletionSummary,
  };
};
